import {
  createMessageAttachmentDownloadUrl,
  fetchMessageAttachmentContentBlob,
} from "../../api/message-attachments-api.js";

export type AttachmentMediaSource =
  | { kind: "url"; url: string }
  | { kind: "stored"; organizationId: number; attachmentId: string };

export function attachmentMediaSourceFrom(
  organizationId: number,
  attachmentId: string | undefined,
  url: string | undefined,
): AttachmentMediaSource | null {
  if (attachmentId) {
    return { kind: "stored", organizationId, attachmentId };
  }
  if (url) {
    return { kind: "url", url };
  }
  return null;
}

export function attachmentDownloadFilename(
  fileName: string | null | undefined,
  mimeType: string | null | undefined,
  fallback = "image",
): string {
  const trimmed = fileName?.trim();
  if (trimmed) {
    return trimmed;
  }
  const ext = mimeType?.split("/")[1]?.split("+")[0];
  if (!ext) return `${fallback}.jpg`;
  if (ext === "jpeg") return `${fallback}.jpg`;
  return `${fallback}.${ext}`;
}

function launchDirectDownload(url: string): void {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.rel = "noopener";
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

async function downloadStoredAttachment(
  organizationId: number,
  attachmentId: string,
  signal?: AbortSignal,
): Promise<void> {
  const response = await createMessageAttachmentDownloadUrl(
    organizationId,
    attachmentId,
    { disposition: "attachment", signal },
  );
  launchDirectDownload(response.url);
}

async function fetchUrlAttachmentBlob(url: string, signal?: AbortSignal): Promise<Blob> {
  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error("fetch_failed");
  }
  return response.blob();
}

export async function downloadAttachmentFromSource(
  source: AttachmentMediaSource,
  fileName: string,
  signal?: AbortSignal,
): Promise<void> {
  if (source.kind === "stored") {
    await downloadStoredAttachment(source.organizationId, source.attachmentId, signal);
    return;
  }

  const blob = await fetchUrlAttachmentBlob(source.url, signal);
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.rel = "noopener";
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

/** Fetches same-origin attachment bytes for preview/playback only. */
export async function fetchStoredAttachmentContentBlob(
  organizationId: number,
  attachmentId: string,
  signal?: AbortSignal,
): Promise<Blob> {
  return fetchMessageAttachmentContentBlob(organizationId, attachmentId, signal);
}

/** @deprecated Prefer downloadAttachmentFromSource for STORED attachments. */
export async function downloadAttachmentFromUrl(
  url: string,
  fileName: string,
): Promise<void> {
  await downloadAttachmentFromSource({ kind: "url", url }, fileName);
}
